import { useEffect, useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, Textarea } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { todayIso } from '@/lib/dates';
import { useT } from '@/lib/i18n';

/**
 * Void (document 05 ConfirmDialog): explains the ledger effect and asks for a date and an
 * audited reason. The caller runs the request.
 */
export function VoidDialog({
  open,
  title,
  description,
  minDate,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  minDate: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (date: string, reason: string) => void;
}) {
  const t = useT();
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setDate(todayIso() < minDate ? minDate : todayIso());
      setReason('');
    }
  }, [open, minDate]);
  // the API moves an earlier date to the document date without saying so: say it here
  const dateError = !date
    ? t('salesPages.document.enterVoidDate')
    : date < minDate
      ? t('salesPages.document.voidBeforeDocument')
      : undefined;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <Field
          label={t('salesPages.document.voidDate')}
          hint={t('salesPages.document.voidDateHint')}
          error={dateError}
        >
          {(id) => <DateInput id={id} value={date} onChange={setDate} />}
        </Field>
        <Field label={t('salesPages.document.reason')} hint={t('salesPages.document.reasonHint')}>
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={pending || Boolean(dateError) || reason.trim().length < 3}
            onClick={() => onConfirm(date, reason.trim())}
          >
            {pending ? t('salesPages.document.voiding') : t('salesPages.document.void')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
